import React from "react";
import { twMerge } from "tailwind-merge";
import ConfirmationDialog from "../components/ConfirmationDialog";
import Map from "../components/Map";
import Rating from "../components/Rating";
import Timeline from "../components/Timeline";
import useServiceDetails from '../hooks/logics/services/use-service-details';
import { normalizedWebsite } from '../utils/helpers';
import { serviceTypeMap, statusMap } from "./ServicesDashboard";

const InfoRow = ({ label, value, element }: { label: string; value?: string; element?: React.ReactNode }) => (
  <div className="flex items-start gap-2 mb-3">
    <h3 className="font-semibold text-gray-600 md:min-w-[140px] min-w-[60px] text-sm">{label}</h3>
    {element ? element : <p className="text-gray-900 text-sm break-all">{value ?? "N/A"}</p>}
  </div>
);

const InfoSection = ({ heading, children }: { heading: string; children: React.ReactNode }) => (
  <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-primary">
    <h1 className="text-base md:text-lg font-bold mb-4 text-gray-800 border-b border-gray-200 pb-2">
      {heading}
    </h1>
    {children}
  </div>
);

const ServiceDetails = () => {
  const {
    id,
    serviceInfo,
    loading,
    isCancellingService,
    selectedServiceId,
    setSelectedServiceId,
    handleServiceCancel,
    isMapVisible,
  } = useServiceDetails();

  const isPackage = serviceInfo?.serviceMetaType === "package_pickup_meta";
  const isCurbside = serviceInfo?.serviceMetaType === "curbside_pickup_meta";
  const isInstore = serviceInfo?.serviceMetaType === "instore_pickup_meta";

  return (
    <>
      <div className="flex-col bg-gray-100 min-h-screen py-6 md:py-10 flex justify-center">
        <div className="flex mb-6 justify-end">
          {!loading &&
            serviceInfo?.status &&
            serviceInfo?.status !== "ratedByUser" &&
            serviceInfo?.status !== "cancelled" &&
            serviceInfo?.status !== "delivered" &&

            (
              <button
                onClick={() => setSelectedServiceId(id!)}
                className="text-sm md:text-base text-red-500 border border-red-500 rounded-lg px-3 py-1 hover:bg-red-50 transition-colors"
              >
                Cancel order
              </button>
            )}
        </div>

        {loading ? (
          <div className="w-full flex items-center justify-center py-20">
            <div className="loader"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">


            <InfoSection heading="General">
              {!isPackage && (
                <>
                  <InfoRow label="Description:" value={serviceInfo?.serviceMeta?.pickupLocation?.description} />
                  <InfoRow label="No of items:" value={serviceInfo?.serviceMeta?.pickupLocation?.numOfItems} />
                  <InfoRow label="Store name:" value={serviceInfo?.serviceMeta?.pickupLocation?.storeName} />
                  <InfoRow label="Store website:" value={serviceInfo?.serviceMeta?.pickupLocation?.storeWebsite} />
                </>
              )}
              {isCurbside && (
                <InfoRow
                  label="Link for confirmation:"
                  value={
                    serviceInfo?.serviceMeta?.pickupLocation?.linkForConfirmation &&
                      serviceInfo?.serviceMeta?.pickupLocation?.linkForConfirmation !== "N/A" ? (
                      <a
                        href={normalizedWebsite(
                          serviceInfo.serviceMeta.pickupLocation.linkForConfirmation
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                      >
                        {serviceInfo.serviceMeta.pickupLocation.linkForConfirmation}
                      </a>
                    ) : (
                      serviceInfo?.serviceMeta?.pickupLocation?.linkForConfirmation
                    )
                  }
                />
              )}
              {isInstore && (
                <InfoRow label="Pickup instructions:" value={serviceInfo?.serviceMeta?.pickupInstruction} />
              )}
              <InfoRow label="Type:" value={serviceTypeMap[serviceInfo?.serviceMetaType] ?? "N/A"} />
              <InfoRow
                label="Status:"
                element={
                  <span className={twMerge(
                    "text-white text-xs rounded-full px-3 py-1 font-medium",
                    (serviceInfo?.status === "ratedByUser" || serviceInfo?.status === "delivered") ? "bg-green-500" :
                      serviceInfo?.status === "cancelled" ? "bg-red-500" : "bg-yellow-500"
                  )}>
                    {statusMap(serviceInfo?.status, "") ?? "N/A"}
                  </span>
                }
              />
              <InfoRow label="Promo code:" value={serviceInfo?.promoCode?.promoCode} />
              {serviceInfo?.ratingByUser && (
                <InfoRow label="Rating by user:" element={<Rating rating={serviceInfo.ratingByUser.rating} />} />
              )}
              {serviceInfo?.ratingByDriver && (
                <InfoRow label="Rating by driver:" element={<Rating rating={serviceInfo.ratingByDriver.rating} />} />
              )}
              {serviceInfo?.serviceMeta?.pickupLocation?.proofOfPurchase && (
                <a href={serviceInfo.serviceMeta.pickupLocation.proofOfPurchase} target="_blank" rel="noreferrer" className="inline-block mt-2">
                  <button className="bg-primary rounded-lg px-6 py-2 text-white text-sm hover:opacity-90 transition-opacity">
                    Proof of purchase
                  </button>
                </a>
              )}
            </InfoSection>


            <InfoSection heading="User">
              <InfoRow label="Name:" value={serviceInfo?.user?.fullName} />
              <InfoRow label="Email:" value={serviceInfo?.user?.email} />
              <InfoRow label="Phone:" value={serviceInfo?.user?.phone} />
            </InfoSection>


            <InfoSection heading="Driver">
              <InfoRow label="Name:" value={serviceInfo?.driver?.fullName} />
              <InfoRow label="Email:" value={serviceInfo?.driver?.email} />
              <InfoRow label="Phone:" value={serviceInfo?.driver?.phone} />
              {serviceInfo?.serviceCompletion ? (
                <a href={serviceInfo.serviceCompletion.secureLocation?.image} target="_blank" rel="noreferrer" className="inline-block mt-2">
                  <button className="bg-primary rounded-lg px-6 py-2 text-white text-sm hover:opacity-90 transition-opacity">
                    Proof of completion
                  </button>
                </a>
              ) : (
                <InfoRow label="Order completion pin:" value={serviceInfo?.pin} />
              )}
            </InfoSection>

            <InfoSection heading="Payment">
              <InfoRow label="User's total:" value={serviceInfo?.userTotal ? `$${serviceInfo.userTotal.toFixed(2)}` : 'N/A'} />
              <InfoRow label="Discounted total:" value={serviceInfo?.discountedUserTotal ? `$${serviceInfo.discountedUserTotal.toFixed(2)}` : 'N/A'} />
              <InfoRow label="Driver's total:" value={serviceInfo?.driverTotal ? `$${serviceInfo.driverTotal.toFixed(2)}` : 'N/A'} />
              <InfoRow label="Swyvery total:" value={serviceInfo?.swyveryTotal ? `$${serviceInfo.swyveryTotal}` : 'N/A'} />
              <InfoRow label="Tip:" value={serviceInfo?.tip ? `$${serviceInfo.tip.toFixed(2)}` : 'N/A'} />
            </InfoSection>

            <InfoSection heading="Pickup location">
              <InfoRow label="State:" value={serviceInfo?.serviceMeta?.pickupLocation?.state} />
              <InfoRow label="City:" value={serviceInfo?.serviceMeta?.pickupLocation?.city} />
              <InfoRow label="Address:" value={serviceInfo?.serviceMeta?.pickupLocation?.address} />
              <InfoRow label="Country:" value={serviceInfo?.serviceMeta?.pickupLocation?.country} />
              {isPackage && (
                <>
                  <InfoRow label="Sender name:" value={serviceInfo?.serviceMeta?.pickupLocation?.firstName ? `${serviceInfo?.serviceMeta?.pickupLocation?.firstName} ${serviceInfo?.serviceMeta?.pickupLocation?.lastName}` : 'N/A'} />
                  <InfoRow label="Sender number:" value={serviceInfo?.serviceMeta?.pickupLocation?.phoneNumber} />
                </>
              )}
              {serviceInfo?.serviceMeta?.pickupLocation?.addressCtd && (
                <InfoRow label="Apartment no:" value={serviceInfo.serviceMeta.pickupLocation.addressCtd} />
              )}
            </InfoSection>


            <InfoSection heading="Delivery location">
              <InfoRow label="State:" value={serviceInfo?.serviceMeta?.deliveryLocation?.state} />
              <InfoRow label="City:" value={serviceInfo?.serviceMeta?.deliveryLocation?.city} />
              <InfoRow label="Address:" value={serviceInfo?.serviceMeta?.deliveryLocation?.address} />
              <InfoRow label="Country:" value={serviceInfo?.serviceMeta?.deliveryLocation?.country} />
              <InfoRow label="Recipient name:" value={serviceInfo?.serviceMeta?.deliveryLocation?.contactName} />
              <InfoRow label="Recipient number:" value={serviceInfo?.serviceMeta?.deliveryLocation?.phone} />
              {serviceInfo?.serviceMeta?.deliveryLocation?.addressCtd && (
                <InfoRow label="Apartment no:" value={serviceInfo.serviceMeta.deliveryLocation.addressCtd} />
              )}
            </InfoSection>

          </div>
        )}

        {isMapVisible && !loading && (
          <div className="w-full bg-white rounded-xl shadow-sm flex mt-4 overflow-hidden">
            <div className="relative w-full h-64 md:h-96">
              <Map
                lngg={serviceInfo?.serviceMeta?.deliveryLocation?.lng}
                latt={serviceInfo?.serviceMeta?.deliveryLocation?.lat}
                pickupLatt={serviceInfo?.serviceMeta?.pickupLocation?.lat}
                pickupLngg={serviceInfo?.serviceMeta?.pickupLocation?.lng}
              />
            </div>
          </div>
        )}

        {serviceInfo?.logs?.length > 0 && (
          <div className="mt-6">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4">Logs</h1>
            <div className="w-full bg-white rounded-xl shadow-sm px-4 md:px-10 py-6 md:py-10">
              <Timeline logs={serviceInfo.logs} />
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        isSubmitButtonDisabled={isCancellingService}
        isSubmitting={isCancellingService}
        handleClick={handleServiceCancel}
        isVisible={selectedServiceId !== ""}
        onHide={() => setSelectedServiceId("")}
        text="Are you sure you want to cancel this service?"
      />
    </>
  );
};

export default ServiceDetails;